import { useEffect, useRef, useState } from 'react';
import {
	Box,
	Checkbox,
	VStack,
	Input,
	Button,
	HStack,
	IconButton,
	Text,
	AlertDialog,
	AlertDialogOverlay,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogBody,
	AlertDialogFooter,
	UnorderedList,
	ListItem,
	Flex,
	Divider,
	Center,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, CheckIcon } from '@chakra-ui/icons';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

const GET_REJECTION_DESCRIPTION = gql`
	query GetRejectionDescription($category: AgentRejectionTypes!) {
		getRejectionDescription(category: $category) {
			id
			category
			description
		}
	}
`;

const ADD_REJECTION_DESCRIPTION = gql`
	mutation AddRejectionDescription($category: AgentRejectionTypes!, $description: String!) {
		addRejectionDescription(category: $category, description: $description)
	}
`;

const UPDATE_REJECTION_DESCRIPTION = gql`
	mutation UpdateRejectionDescription($id: String!, $description: String!) {
		updateRejectionDescription(id: $id, description: $description)
	}
`;

const DELETE_REJECTION_DESCRIPTION = gql`
	mutation DeleteRejectionDescription($id: String!) {
		deleteRejectionDescription(id: $id)
	}
`;

export default function DescriptionSelector({ selected, setSelected, customDescriptions, setCustomDescriptions, category }) {
	const { t } = useTranslation();
	const [newDesc, setNewDesc] = useState('');
	const [editingIndex, setEditingIndex] = useState(null);
	const [editingValue, setEditingValue] = useState('');
	const [editingSource, setEditingSource] = useState(null);
	const [fetchedDescriptions, setFetchedDescriptions] = useState([]);
	const [deleteId, setDeleteId] = useState(null);

	const cancelRef = useRef();

	const [getRejectionDescription] = useLazyQuery(GET_REJECTION_DESCRIPTION, {
		onCompleted: (data) => {
			if (!data?.getRejectionDescription) return;
			setFetchedDescriptions(data?.getRejectionDescription);
			const validDescriptions = data?.getRejectionDescription.map((d) => d.description);
			const combined = [...new Set([...validDescriptions, ...customDescriptions])];
			setSelected((prev) => prev.filter((desc) => combined.includes(desc)));
		},
		fetchPolicy: 'network-only',
	});

	const [addRejectionDescription] = useMutation(ADD_REJECTION_DESCRIPTION);
	const [updateRejectionDescription] = useMutation(UPDATE_REJECTION_DESCRIPTION);
	const [deleteRejectionDescription] = useMutation(DELETE_REJECTION_DESCRIPTION);

	useEffect(() => {
		if (category) getRejectionDescription({ variables: { category } });
		else setFetchedDescriptions([]);
	}, [category]);

	useEffect(() => {
		const unique = Array.from(new Set(customDescriptions));
		if (unique.length !== customDescriptions.length) {
			setCustomDescriptions(unique);
		}
	}, [customDescriptions]);

	const normalizeDesc = (desc) => desc.trim().toLowerCase();

	const toggleDescription = (desc) => {
		setSelected((prev) => {
			const normalized = normalizeDesc(desc);
			const filtered = prev.filter((d) => normalizeDesc(d) !== normalized);
			return prev.length === filtered.length ? [...prev, desc.trim()] : filtered;
		});
	};

	const addCustomDescription = async () => {
		const trimmed = newDesc.trim();
		if (!trimmed || !category) return;

		const allDescriptions = [...fetchedDescriptions.map((d) => d.description), ...customDescriptions];
		if (allDescriptions.map(normalizeDesc).includes(normalizeDesc(trimmed))) return;

		try {
			const { data } = await addRejectionDescription({ variables: { category, description: trimmed } });

			if (data?.addRejectionDescription) {
				setFetchedDescriptions((prev) => [...prev, { id: data.addRejectionDescription, category, description: trimmed }]);
			}
		} catch (err) {
			console.error(`Failed to save: ${trimmed}`, err);
		}

		setNewDesc('');
	};

	const startEditing = (index, value, source) => {
		setEditingIndex(index);
		setEditingValue(value);
		setEditingSource(source);
	};

	const saveEdit = async (index) => {
		const trimmed = editingValue.trim();
		if (!trimmed) return;

		if (editingSource === 'custom') {
			setCustomDescriptions((prev) => {
				if (prev.some((d, i) => i !== index && normalizeDesc(d) === normalizeDesc(trimmed))) return prev;
				const updated = [...prev];
				const old = updated[index];
				updated[index] = trimmed;
				setSelected((sel) => sel.map((d) => (normalizeDesc(d) === normalizeDesc(old) ? trimmed : d)));
				return updated;
			});
		} else if (editingSource === 'fetched') {
			const old = fetchedDescriptions[index];
			await updateRejectionDescription({ variables: { id: old.id, description: trimmed } });
			setFetchedDescriptions((prev) => {
				const updated = [...prev];
				updated[index] = { ...updated[index], description: trimmed };
				return updated;
			});
			setSelected((sel) => sel.map((d) => (normalizeDesc(d) === normalizeDesc(old.description) ? trimmed : d)));
		}

		setEditingIndex(null);
		setEditingValue('');
		setEditingSource(null);
	};

	const deleteCustomDescription = (desc) => {
		setCustomDescriptions((prev) => prev.filter((d) => d !== desc));
		setSelected((prev) => prev.filter((d) => normalizeDesc(d) !== normalizeDesc(desc)));
	};

	const confirmDeleteFetchedDescription = async () => {
		await deleteRejectionDescription({ variables: { id: deleteId } });
		const deleted = fetchedDescriptions.find((d) => d.id === deleteId);
		setFetchedDescriptions((prev) => prev.filter((d) => d.id !== deleteId));
		setSelected((prev) => prev.filter((d) => normalizeDesc(d) !== normalizeDesc(deleted?.description)));
		setDeleteId(null);
	};

	return (
		<>
			<HStack mb={4}>
				<Input width="400px" placeholder={t('Add a custom description')} value={newDesc} onChange={(e) => setNewDesc(e.target.value)} />
				<Button onClick={addCustomDescription} disabled={!newDesc.trim()} colorScheme="blue">
					{t('Add')}
				</Button>
			</HStack>

			<Flex align="start" wrap="wrap">
				<Box width="50%" pr={2}>
					<Text fontWeight="bold" mb={2}>
						{t('Select Descriptions:')}
					</Text>

					<VStack align="start" width="105%" maxHeight="350px" overflowY="auto" pl={1} pr="30px">
						{/* Fetched Descriptions */}
						{fetchedDescriptions?.map((desc, idx) => {
							const isEditing = editingIndex === idx && editingSource === 'fetched';
							return (
								<HStack key={desc.id} width="100%" align="center" textAlign="justify">
									<Checkbox isChecked={selected.includes(desc?.description)} onChange={() => toggleDescription(desc?.description)} />
									{isEditing ? (
										<Input
											size="sm"
											value={editingValue}
											onChange={(e) => setEditingValue(e.target.value)}
											onKeyDown={(e) => e.key === 'Enter' && saveEdit(idx)}
											flex="1"
										/>
									) : (
										<Text flex="1" fontSize="sm" _hover={{ bg: '#ecf0f1' }} wordBreak="break-word" whiteSpace="pre-wrap">
											<Text as="span" fontWeight="semibold" mr={1}>
												{idx + 1}.
											</Text>
											{desc?.description}
										</Text>
									)}
									{isEditing ? (
										<IconButton icon={<CheckIcon />} size="sm" onClick={() => saveEdit(idx)} aria-label="Save" colorScheme="green" />
									) : (
										<>
											<IconButton
												icon={<EditIcon />}
												size="sm"
												onClick={() => startEditing(idx, desc?.description, 'fetched')}
												aria-label="Edit"
											/>
											<IconButton
												icon={<DeleteIcon />}
												size="sm"
												colorScheme="red"
												onClick={() => setDeleteId(desc?.id)}
												aria-label="Delete"
											/>
										</>
									)}
								</HStack>
							);
						})}

						{/* Custom Descriptions */}
						{customDescriptions?.map((desc, idx) => {
							const isEditing = editingIndex === idx && editingSource === 'custom';
							return (
								<HStack key={desc} width="100%" align="start">
									<Checkbox isChecked={selected.includes(desc)} onChange={() => toggleDescription(desc)} />
									{isEditing ? (
										<Input
											size="sm"
											value={editingValue}
											onChange={(e) => setEditingValue(e.target.value)}
											onKeyDown={(e) => e.key === 'Enter' && saveEdit(idx)}
										/>
									) : (
										<Text flex="1" fontSize="sm">
											{desc}
										</Text>
									)}
									{isEditing ? (
										<IconButton icon={<CheckIcon />} size="sm" onClick={() => saveEdit(idx)} aria-label="Save" colorScheme="green" />
									) : (
										<>
											<IconButton icon={<EditIcon />} size="sm" onClick={() => startEditing(idx, desc, 'custom')} aria-label="Edit" />
											<IconButton
												icon={<DeleteIcon />}
												size="sm"
												colorScheme="red"
												onClick={() => deleteCustomDescription(desc)}
												aria-label="Delete"
											/>
										</>
									)}
								</HStack>
							);
						})}
					</VStack>
				</Box>

				<Center height="385px" ml={5}>
					<Divider orientation="vertical" borderColor="gray" />
				</Center>

				{/* Selected List */}
				<Box width="42%" pl={4} align="start" textAlign="justify" maxHeight="370px" overflowY="auto" pr="30px">
					<Text fontWeight="bold" mb={2}>
						{t('Selected Descriptions:')}
					</Text>
					<UnorderedList>
						{selected.map((desc, idx) => (
							<ListItem key={idx} mt={2}>
								{desc}
							</ListItem>
						))}
					</UnorderedList>
				</Box>
			</Flex>

			<AlertDialog isOpen={!!deleteId} leastDestructiveRef={cancelRef} onClose={() => setDeleteId(null)}>
				<AlertDialogOverlay>
					<AlertDialogContent>
						<AlertDialogHeader fontSize="lg" fontWeight="bold">
							{t('Delete Description')}
						</AlertDialogHeader>
						<AlertDialogBody>{t('Are you sure you want to delete this description? This action cannot be undone.')}</AlertDialogBody>
						<AlertDialogFooter>
							<Button ref={cancelRef} onClick={() => setDeleteId(null)}>
								{t('Cancel')}
							</Button>
							<Button colorScheme="red" onClick={confirmDeleteFetchedDescription} ml={3}>
								{t('Delete')}
							</Button>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialogOverlay>
			</AlertDialog>
		</>
	);
}
